import React from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import PulseLoaderComponent from '~/prix/react/components/pulseLoader'
import * as Sentry from '@sentry/react'

import HomeScreen from '~/packages/home/home.screen'
import NotFound from './notFound/notFound.screen'
import TermsScreen from './terms/terms.screen'
import OptionsScreen from './option/option.screen'
import TimeSeriesScreen from './timeSeries/timeSeries.screen'
import SystemAndInformation from './systemAndInformation/systemAndInformation'
import AttendanceRadarMap from './legalEntityGeoprocessing/radar/attendanceRadarMap.screen'
import DisproportionateAttentionMap from './legalEntityAttendance/disproportionateAttention/map/disproportionateAttentionMapFromURL.screen'
import AttendantProductionSelectableChart from './attendant/productionChart/attendantProductionSelectableChart.screen'
import AttendantProductionIndividualChart from './attendant/productionChart/attendantProductionIndividualChart.screen'
import AttendantSelectableMap from './attendant/map/attendantSelectableMap.screen'
import AttendantIndividualMap from './attendant/map/attendantIndividualMap.screen'
import MapDemarcation from './legalEntityGeoprocessing/mapDemarcation/legalEntityGeoprocessingMapDemarcation.screen'
import MapDemarcationPolygon from './legalEntityGeoprocessing/mapDemarcation/legalEntityGeoprocessingMapDemarcationPolygon.screen'
import MapDemarcationRadius from './legalEntityGeoprocessing/mapDemarcation/legalEntityGeoprocessingMapDemarcationRadius.screen'
import MapDemarcationRegion from './legalEntityGeoprocessing/mapDemarcation/legalEntityGeoprocessingMapDemarcationRegion.screen'
import ReportReadScreen from './report/read/reportRead.screen'
import AgentReportScreen from './agentReport/agentReport.screen'
import NotificationSettingsScreen from './notifications/settings/notificationSettings.screen'
import MonitorUserPermissionsScreen from './monitor/userPermissions/monitorUserPermissions.screen'

import SearchLegalEntity from './searchLeaglEntity/searchLegalEntity.screen'
import GeneralLlmChatBaseScreen from './llmChat/llmChatBase.screen'
import GeneralLlmChatGetScreen from './llmChat/get/llmChatGet.screen'
import ReportsAndStudiesScreen from './reportsAndStudies/reportsAndStudies.screen'
import LegalEntitySizeScreen from './legalEntitySize/legalEntitySize.screen'
import AnalyticsScreenTracker from '~/utils/client/analytics/analyticsScreenTracker'

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const ExplorerScreen = React.lazy(() => import('./explorer/explorer.screen'))
const MapScreen = React.lazy(
  () => import('./legalEntityGeoprocessing/map/legalEntityGeoprocessingMapFromURL.screen'),
)
const WelcomeScreen = React.lazy(() => import('./welcome/welcome.screen'))
const SentinelScreen = React.lazy(() => import('./sentinel/sentinel.screen'))
const GeneralLlmChatCreateScreen = React.lazy(() => import('./llmChat/create/llmChatCreate.screen'))
const InsightListScreen = React.lazy(() => import('./insight/insightList.screen'))
const AboutScreen = React.lazy(() => import('./about/about.screen'))

export default function Router() {
  return (
    <BrowserRouter>
      <AnalyticsScreenTracker />
      <React.Suspense fallback={<PulseLoaderComponent />}>
        <SentryRoutes>
          <Route path='/' element={<HomeScreen />} />
          <Route path='/index.html' element={<HomeScreen />} />
          <Route path='/terms' element={<TermsScreen />} />
          <Route path='/app'>
            <Route index element={<WelcomeScreen />} />
            <Route path='/app/sentinel' element={<SentinelScreen />} />
            <Route path='/app/map' element={<MapScreen />} />
            <Route path='/app/map/demarcation' element={<MapDemarcation />} />
            <Route
              path='/app/map/demarcation/polygon/:geohashes'
              element={<MapDemarcationPolygon />}
            />
            <Route
              path='/app/map/demarcation/radius/:geohash/:radius'
              element={<MapDemarcationRadius />}
            />
            <Route
              path='/app/map/demarcation/region/:geo/:ids'
              element={<MapDemarcationRegion />}
            />
            <Route path='/app/map/:by/:id' element={<MapScreen />} />
            <Route path='/app/map/:by/:id/:childrenGeoLevel' element={<MapScreen />} />
            <Route path='/app/time-series' element={<TimeSeriesScreen />} />
            <Route path='/app/time-series/:timeSeriesKey' element={<TimeSeriesScreen />} />
            <Route
              path='/app/time-series/:timeSeriesKey/sas/:sasCode'
              element={<TimeSeriesScreen />}
            />
            <Route path='/app/map/legal-entity-size' element={<LegalEntitySizeScreen />} />
            <Route
              path='/app/time-series/:timeSeriesKey/sas/:sasCode/:eventDate'
              element={<TimeSeriesScreen />}
            />
            <Route
              path='/app/time-series/:timeSeriesKey/state/:state'
              element={<TimeSeriesScreen />}
            />
            <Route
              path='/app/time-series/:timeSeriesKey/sas/:sasCode/state/:state'
              element={<TimeSeriesScreen />}
            />
            <Route
              path='/app/time-series/:timeSeriesKey/sas/:sasCode/state/:state/:eventDate'
              element={<TimeSeriesScreen />}
            />
            <Route
              path='/app/time-series/:timeSeriesKey/state/:state/:eventDate'
              element={<TimeSeriesScreen />}
            />
            <Route path='/app/options' element={<OptionsScreen />} />
            <Route path='/app/reports-and-studies' element={<ReportsAndStudiesScreen />} />
            <Route path='/app/explorer' element={<ExplorerScreen />} />
            <Route path='/app/explorer/:actionKey/:triggerIndex' element={<ExplorerScreen />} />
            <Route
              path='/app/options/monitor-permissions'
              element={<MonitorUserPermissionsScreen />}
            />
            <Route path='/app/options/system-and-information' element={<SystemAndInformation />} />
            <Route path='/app/options/notifications' element={<NotificationSettingsScreen />} />
            <Route path='/app/searchLegalEntity' element={<SearchLegalEntity />} />
            <Route
              path='/app/searchLegalEntity/:searchLegalEntityId'
              element={<SearchLegalEntity />}
            />
            <Route path='/app/attendance-radar' element={<AttendanceRadarMap />} />
            <Route path='/app/report/:key' element={<ReportReadScreen />} />
            <Route path='/app/report/:key/:hash' element={<ReportReadScreen />} />
            <Route
              path='/app/disproportionate-attention'
              element={<DisproportionateAttentionMap />}
            />
            <Route
              path='/app/disproportionate-attention/:by/:id'
              element={<DisproportionateAttentionMap />}
            />
            <Route
              path='/app/disproportionate-attention/:by/:id/:childrenGeoLevel'
              element={<DisproportionateAttentionMap />}
            />
            <Route
              path='/app/attendant-production-selectable-chart'
              element={<AttendantProductionSelectableChart />}
            />
            <Route
              path='/app/attendant-production-individual-chart'
              element={<AttendantProductionIndividualChart />}
            />
            <Route path='/app/attendant-selectable-map' element={<AttendantSelectableMap />} />
            <Route path='/app/attendant-individual-map' element={<AttendantIndividualMap />} />
            <Route path='/app/agent-report' element={<AgentReportScreen />} />
            <Route path='/app/agent-report/:agentId' element={<AgentReportScreen />} />
            <Route
              path='/app/agent-report/:agentHash/:dataSourceId'
              element={<AgentReportScreen />}
            />
            <Route path='/app/about' element={<AboutScreen />} />
          </Route>
          <Route path='/app/ai' element={<GeneralLlmChatBaseScreen />}>
            <Route index element={<GeneralLlmChatCreateScreen />} />
            <Route path=':chatId' element={<GeneralLlmChatGetScreen />} />
          </Route>
          <Route path='/app/insights' element={<InsightListScreen />} />
          <Route path='*' element={<NotFound />} />
        </SentryRoutes>
      </React.Suspense>
    </BrowserRouter>
  )
}
